import * as React from "react";
import { Link, useLocation, useRouter, useNavigate } from "@tanstack/react-router";
import { BrandLogo } from "@/components/brand-logo";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, GraduationCap, BarChart3, Megaphone,
  Calendar, FileText, ClipboardCheck, BookOpen, Home,
  Bell, BellRing, UserCheck, ChevronLeft, ChevronRight, LogOut, Menu, X, Settings,
  ArrowLeft, CreditCard, Zap, FolderOpen, Lightbulb,
} from "lucide-react";
import { PageGuide } from "@/components/page-guide";
import { findHelpTopicForPath } from "@/lib/help-content";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, useDisplayInfo, ROLE_HOME } from "@/lib/auth";
import { avatarByKey } from "@/lib/avatars";
import { UserAvatar } from "@/components/user-avatar";
import { DemoBanner } from "@/components/demo-banner";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { useEntitlements } from "@/hooks/use-entitlements";
import { ThemeToggle } from "@/lib/theme";

export type PortalKey = "admin" | "teacher" | "student";

type NavItem = { to: string; label: string; icon: React.ComponentType<{ className?: string; strokeWidth?: number }> };

function getNavConfig(
  portal: PortalKey,
  isAdminTeacher: boolean,
  flags: { documentsEnabled: boolean; teacherNotify: boolean },
): { label: string; persona: string; initials: string; items: NavItem[] } {
  if (portal === "admin") {
    return {
      label: "Quản lý",
      persona: "Nguyễn Minh Tâm",
      initials: "MT",
      items: [
        { to: "/admin", label: "Tổng quan", icon: LayoutDashboard },
        { to: "/admin/students", label: "Người dùng", icon: Users },
        { to: "/admin/classes", label: "Lớp học", icon: GraduationCap },
        ...(isAdminTeacher ? [
          { to: "/admin/my-schedule", label: "Lịch dạy", icon: Calendar },
          { to: "/admin/my-attendance", label: "Điểm danh", icon: ClipboardCheck },
          { to: "/admin/my-assignments", label: "Bài tập", icon: FileText },
        ] : []),
        { to: "/admin/reports", label: "Báo cáo", icon: BarChart3 },
        { to: "/admin/announcements", label: "Thông báo", icon: Megaphone },
        ...(flags.documentsEnabled ? [{ to: "/admin/documents", label: "Tài liệu", icon: FolderOpen }] : []),
        { to: "/billing", label: "Gói thanh toán", icon: CreditCard },
        { to: "/admin/help", label: "Hướng dẫn sử dụng", icon: Lightbulb },
        { to: "/settings", label: "Cài đặt", icon: Settings },
      ],
    };
  }
  if (portal === "teacher") {
    return {
      label: "Giáo viên",
      persona: "Cô Lê Phương Anh",
      initials: "PA",
      items: [
        { to: "/teacher", label: "Tổng quan", icon: LayoutDashboard },
        { to: "/teacher/schedule", label: "Lịch dạy", icon: Calendar },
        { to: "/teacher/attendance", label: "Điểm danh", icon: ClipboardCheck },
        { to: "/teacher/assignments", label: "Bài tập", icon: FileText },
        { to: "/teacher/grading", label: "Chấm điểm", icon: BookOpen },
        { to: "/teacher/analytics", label: "Thống kê", icon: BarChart3 },
        ...(flags.documentsEnabled ? [{ to: "/teacher/documents", label: "Tài liệu", icon: FolderOpen }] : []),
        ...(flags.teacherNotify ? [{ to: "/teacher/announcements", label: "Thông báo", icon: Megaphone }] : []),
        { to: "/teacher/help", label: "Hướng dẫn sử dụng", icon: Lightbulb },
        { to: "/settings", label: "Cài đặt", icon: Settings },
      ],
    };
  }
  return {
    label: "Học sinh",
    persona: "Trần Hoàng An",
    initials: "HA",
    items: [
      { to: "/student", label: "Trang chủ", icon: Home },
      { to: "/student/assignments", label: "Bài tập", icon: FileText },
      { to: "/student/schedule", label: "Lịch học", icon: Calendar },
      { to: "/student/attendance", label: "Điểm danh", icon: ClipboardCheck },
      { to: "/student/classes", label: "Lớp học", icon: BookOpen },
        ...(flags.documentsEnabled ? [{ to: "/student/documents", label: "Tài liệu", icon: FolderOpen }] : []),
        { to: "/student/help", label: "Hướng dẫn sử dụng", icon: Lightbulb },
      { to: "/settings", label: "Cài đặt", icon: Settings },
    ],
  };
}


export function PortalLayout({
  portal,
  title,
  breadcrumb,
  children,
}: {
  portal: PortalKey;
  title?: string;
  breadcrumb?: { label: string; to?: string }[];
  children: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const ent = useEntitlements();
  const isAdminTeacher = portal === "admin" && ent.maxTeachers === 0;
  const cfg = getNavConfig(portal, isAdminTeacher, { documentsEnabled: ent.documentsEnabled, teacherNotify: ent.teacherNotify });
  const location = useLocation();
  const router = useRouter();
  const navigate = useNavigate();
  const { user, loading, logout, isDemo } = useAuth();
  const display = useDisplayInfo();
  // Show ALL sidebar items regardless of entitlements; gated pages show upgrade overlay inside.
  const navItems = cfg.items;

  React.useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-portal", portal);
    }
  }, [portal]);

  // Auth guard: kiểm tra session Supabase trực tiếp thay vì state `user`,
  // tránh false-negative khi loadUserFromSupabase còn đang chạy.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (loading) return;
    const hasDemo = !!window.localStorage.getItem("ezyclass.demo.role") || !!window.localStorage.getItem("eduflow.demo.role");
    // Onboarding gate: user thật đã có role nhưng chưa onboarded → ép qua /onboarding
    if (user && !user.onboarded && !hasDemo) {
      if (location.pathname !== "/onboarding") {
        navigate({ to: "/onboarding" });
      }
      return;
    }
    // Role guard: if a signed-in user lands on the wrong portal, send them
    // to their own home. RLS already filters data, but rendering another
    // role's chrome with empty data is confusing and leaks UI structure.
    if (user && user.role && user.role !== portal && !hasDemo) {
      navigate({ to: ROLE_HOME[user.role] });
      return;
    }
    if (hasDemo || user) return;

    let cancelled = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!data.session) {
        navigate({ to: "/login", search: { redirect: location.pathname } });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, location.pathname, user, loading, portal]);

  const displayName = display.name || cfg.persona;
  const displayInitials = displayName
    .split(" ")
    .map((p) => p[0])
    .slice(-2)
    .join("")
    .toUpperCase() || cfg.initials;
  const displayEmail = display.email;

  return (
    <div data-portal={portal} className="min-h-screen flex bg-[oklch(0.985_0.004_247)]">
      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        animate={{ width: collapsed ? 72 : 260 }}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
        className={cn(
          "bg-slate-950 text-white flex flex-col fixed h-screen z-50 ring-1 ring-white/[0.04]",
          mobileOpen ? "left-0" : "-left-full md:left-0",
        )}
        style={{ transition: "left 0.3s ease" }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <BrandLogo size="size-9" onDark />
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-display font-semibold text-[15px] tracking-tight leading-tight truncate">Ezyclass</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{cfg.label}</div>
              </div>
            )}
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto md:hidden p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
            aria-label="Đóng menu"
          ><X className="size-4" strokeWidth={1.5} /></button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active =
              item.to === "/settings"
                ? location.pathname === "/settings"
                : item.to === `/${portal}`
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium",
                  "transition-all duration-300",
                  active
                    ? "bg-white/[0.08] text-white"
                    : "text-white/50 hover:text-white/90 hover:bg-white/[0.04]",
                )}
              >
                {active && (
                  <motion.span
                    layoutId={`sb-${portal}`}
                    className="absolute -left-3 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-blue-500"
                  />
                )}
                <Icon className="size-[18px] shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>


        {/* Plan badge (admin only) */}
        {portal === "admin" && !isDemo && <SidebarPlanBadge collapsed={collapsed} />}

        {/* User */}
        <div className="border-t border-white/[0.06] p-3">
          {isDemo ? (
            <div className="space-y-1">
              {!collapsed && (
                <div className="text-[11px] text-white/40 text-center mb-2">
                  Đang xem demo: <strong className="text-white/70">{cfg.label}</strong>
                </div>
              )}
              <button
                onClick={async () => { await logout(); window.location.href = "/"; }}
                className={cn(
                  "flex items-center gap-2 w-full p-2 rounded-xl text-[13px] font-medium text-white/60 hover:bg-white/[0.06] hover:text-white transition-colors",
                  collapsed && "justify-center"
                )}
                title="Quay lại trang chủ"
              >
                <ArrowLeft className="size-4 shrink-0" strokeWidth={1.5} />
                {!collapsed && <span>Quay lại trang chủ</span>}
              </button>
              <button
                onClick={async () => { await logout(); window.location.href = "/#pricing"; }}
                className={cn(
                  "flex items-center gap-2 w-full p-2 rounded-xl text-[13px] font-medium text-white/60 hover:bg-white/[0.06] hover:text-white transition-colors",
                  collapsed && "justify-center"
                )}
                title="Xem gói mua"
              >
                <CreditCard className="size-4 shrink-0" strokeWidth={1.5} />
                {!collapsed && <span>Xem gói mua</span>}
              </button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn(
                  "flex items-center gap-3 w-full p-2 rounded-xl hover:bg-white/[0.06] transition-colors",
                  collapsed && "justify-center"
                )}>
                  <Avatar className="size-8 shrink-0 ring-1 ring-white/10">
                    {display.avatarUrl ? (
                      <AvatarImage src={display.avatarUrl} alt={displayName} className="object-cover" />
                    ) : display.avatarKey ? (
                      <AvatarFallback className="bg-white/[0.08]" style={{ fontSize: "16px" }}>
                        {avatarByKey(display.avatarKey)?.emoji ?? displayInitials}
                      </AvatarFallback>
                    ) : (
                      <AvatarFallback className="bg-white/[0.08] text-white font-medium text-[11px]">
                        {displayInitials}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  {!collapsed && (
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[13px] font-medium truncate text-white">{displayName}</div>
                      <div className="text-[11px] text-white/40 truncate">{displayEmail ?? cfg.label}</div>
                    </div>
                  )}
                  {!collapsed && <ChevronRight className="size-3.5 text-white/30" strokeWidth={1.5} />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-52 mb-1 rounded-xl">
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal truncate">
                  {displayEmail ?? cfg.label}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="size-4" strokeWidth={1.5} /> Cài đặt tài khoản
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive flex items-center gap-2 cursor-pointer"
                  onClick={async (e) => {
                    e.preventDefault();
                    await logout();
                    window.location.href = "/login";
                  }}
                >
                  <LogOut className="size-4" strokeWidth={1.5} /> Đăng xuất
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden md:flex mt-2 w-full items-center justify-center gap-1 text-[11px] py-1.5 rounded-lg hover:bg-white/[0.06] text-white/40 hover:text-white/70 transition-colors"
          >
            {collapsed ? <ChevronRight className="size-3.5" strokeWidth={1.5} /> : (<><ChevronLeft className="size-3.5" strokeWidth={1.5} />Thu gọn</>)}
          </button>
        </div>


      </motion.aside>

      {/* Main */}
      <div className={cn("flex-1 min-w-0 flex flex-col transition-[margin] duration-500 ease-out", collapsed ? "md:ml-[72px]" : "md:ml-[260px]")}>
        <DemoBanner />
        {/* Top bar */}

        <header className="h-14 bg-white/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-800/80 sticky top-0 z-30 flex items-center px-4 md:px-6 gap-4">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-1.5 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:bg-slate-800 transition-colors"
            aria-label="Mở menu"
          ><Menu className="size-5" strokeWidth={1.5} /></button>

          <div className="flex-1 min-w-0">
            {breadcrumb ? (
              <div className="flex items-center gap-1.5 text-sm">
                {breadcrumb.map((b, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <ChevronRight className="size-3 text-slate-300" strokeWidth={1.5} />}
                    {b.to ? (
                      <Link to={b.to} className="text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:text-white transition-colors">{b.label}</Link>
                    ) : (
                      <span className="font-medium text-slate-900 dark:text-white">{b.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : title ? (
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-slate-400 dark:text-slate-500">{cfg.label}</span>
                <ChevronRight className="size-3 text-slate-300" strokeWidth={1.5} />
                <span className="font-medium text-slate-900 dark:text-white">{title}</span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {portal === "admin" && <UpgradeButton />}
            <ThemeToggle />
            <NotificationBell />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-4 md:px-8 py-6 md:py-8 max-w-[1480px] w-full mx-auto">
          {title && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
              className="mb-6"
            >
              <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">{title}</h1>
            </motion.div>

          )}
          {(() => {
            // Mount inline PageGuide for routes that have help content.
            // /admin (overview) handles its own welcome guide separately to avoid duplicate banners.
            if (location.pathname === "/admin") return null;
            // Help hub pages don't need an inline guide on top of themselves.
            if (location.pathname.endsWith("/help")) return null;
            const match = findHelpTopicForPath(location.pathname);
            if (!match) return null;
            return <PageGuide topic={match.topic} storageKey={match.key} />;
          })()}
          {children}
        </main>
      </div>
      <AdminAnnouncementPopup />

    </div>
  );
}

type NotifRow = {
  id: string;
  title: string;
  body: string;
  created_at: string;
  audience: string;
  isRead: boolean;
};

function NotificationBell() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const notifsQ = useQuery({
    queryKey: ["notifications", user?.id],
    enabled: !!user,
    refetchInterval: 30000,
    queryFn: async (): Promise<NotifRow[]> => {
      const audienceForRole = user!.role === "student" ? "students"
        : user!.role === "teacher" ? "teachers" : "all";
      const nowIso = new Date().toISOString();

      // Compute user's class scope to filter class-targeted announcements
      let myClassIds: string[] = [];
      if (user!.role === "student") {
        const { data: enrolls } = await supabase
          .from("enrollments").select("class_id").eq("student_id", user!.id).eq("is_active", true);
        myClassIds = (enrolls ?? []).map(e => e.class_id);
      } else if (user!.role === "teacher") {
        const { data: classes } = await supabase
          .from("classes").select("id").eq("teacher_id", user!.id);
        myClassIds = (classes ?? []).map(c => c.id);
      }

      let query = supabase
        .from("announcements")
        .select("id, title, body, created_at, audience, scheduled_at")
        .or(`audience.eq.all,audience.eq.${audienceForRole}`)
        .or(`scheduled_at.is.null,scheduled_at.lte.${nowIso}`)
        .or(`target_user_id.is.null,target_user_id.eq.${user!.id}`)
        .order("created_at", { ascending: false })
        .limit(20);

      if (myClassIds.length > 0) {
        query = query.or(`class_id.is.null,class_id.in.(${myClassIds.join(",")})`);
      } else {
        query = query.is("class_id", null);
      }

      const { data: anns } = await query;
      const { data: reads } = await supabase
        .from("notification_reads")
        .select("announcement_id")
        .eq("user_id", user!.id);
      const readSet = new Set((reads ?? []).map((r) => r.announcement_id));
      return (anns ?? []).map((a) => ({ ...a, isRead: readSet.has(a.id) }));
    },

  });

  const notifs = notifsQ.data ?? [];
  const unreadCount = notifs.filter((n) => !n.isRead).length;

  const markRead = async (id: string) => {
    if (!user) return;
    await supabase.from("notification_reads").upsert(
      { user_id: user.id, announcement_id: id },
      { onConflict: "user_id,announcement_id" },
    );
    qc.invalidateQueries({ queryKey: ["notifications", user.id] });
  };

  const markAllRead = async () => {
    if (!user) return;
    const unread = notifs.filter((n) => !n.isRead);
    if (!unread.length) return;
    await supabase.from("notification_reads").upsert(
      unread.map((n) => ({ user_id: user.id, announcement_id: n.id })),
      { onConflict: "user_id,announcement_id" },
    );
    qc.invalidateQueries({ queryKey: ["notifications", user.id] });
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Vừa xong";
    if (mins < 60) return `${mins} phút trước`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} giờ trước`;
    return `${Math.floor(hrs / 24)} ngày trước`;
  };

  const NotifIcon = ({ audience }: { audience: string }) => {
    if (audience === "all") return <Megaphone className="size-4 text-blue-500 mt-0.5 shrink-0" />;
    if (audience === "students") return <GraduationCap className="size-4 text-amber-500 mt-0.5 shrink-0" />;
    if (audience === "teachers") return <UserCheck className="size-4 text-emerald-500 mt-0.5 shrink-0" />;
    return <BellRing className="size-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-muted transition-colors" aria-label="Thông báo">
          <Bell className="size-5" />
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              className="absolute -top-0.5 -right-0.5 size-4 rounded-full text-[10px] font-bold grid place-items-center text-white"
              style={{ background: "var(--portal-accent)" }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </motion.span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>
            Thông báo {unreadCount > 0 && (
              <span className="text-xs text-muted-foreground font-normal">({unreadCount} chưa đọc)</span>
            )}
          </span>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline font-normal">
              Đánh dấu tất cả đã đọc
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifsQ.isLoading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">Đang tải...</div>
        ) : notifs.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            <Bell className="size-6 mx-auto mb-2 text-muted-foreground" />
            Chưa có thông báo nào
          </div>
        ) : (
          notifs.map((n) => (
            <DropdownMenuItem
              key={n.id}
              className={cn(
                "flex items-start gap-2.5 py-3 cursor-pointer",
                !n.isRead && "bg-muted/40",
              )}
              onClick={() => markRead(n.id)}
            >
              <NotifIcon audience={n.audience} />
              <div className="min-w-0 flex-1">
                <div className={cn("text-sm leading-tight", !n.isRead ? "font-semibold" : "font-medium")}>{n.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
              </div>
              {!n.isRead && (
                <div className="size-2 rounded-full mt-1.5 shrink-0" style={{ background: "var(--portal-accent)" }} />
              )}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AdminAnnouncementPopup() {
  const { user } = useAuth();
  const [popups, setPopups] = React.useState<{ id: string; title: string; body: string; created_at: string }[]>([]);

  React.useEffect(() => {
    if (!user) return;
    const today = new Date().toISOString().slice(0, 10);
    const storageKey = `ezyclass.popup_shown.${user.id}`;
    try {
      if (localStorage.getItem(storageKey) === today) return;
    } catch { /* noop */ }

    let cancelled = false;
    (async () => {
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const nowIso = new Date().toISOString();
      const audienceForRole = user.role === "student" ? "students" : user.role === "teacher" ? "teachers" : "all";

      // Compute user's class scope (mirror NotificationBell)
      let myClassIds: string[] = [];
      if (user.role === "student") {
        const { data: enrolls } = await supabase
          .from("enrollments").select("class_id").eq("student_id", user.id).eq("is_active", true);
        myClassIds = (enrolls ?? []).map(e => e.class_id);
      } else if (user.role === "teacher") {
        const { data: classes } = await supabase
          .from("classes").select("id").eq("teacher_id", user.id);
        myClassIds = (classes ?? []).map(c => c.id);
      }

      let query = supabase
        .from("announcements")
        .select("id, title, body, created_at, audience, scheduled_at")
        .or(`audience.eq.all,audience.eq.${audienceForRole}`)
        .gte("created_at", cutoff)
        .or(`scheduled_at.is.null,scheduled_at.lte.${nowIso}`)
        .or(`target_user_id.is.null,target_user_id.eq.${user.id}`)
        .order("created_at", { ascending: false })
        .limit(10);

      if (myClassIds.length > 0) {
        query = query.or(`class_id.is.null,class_id.in.(${myClassIds.join(",")})`);
      } else {
        query = query.is("class_id", null);
      }

      const { data: anns } = await query;
      const { data: reads } = await supabase
        .from("notification_reads").select("announcement_id").eq("user_id", user.id);
      const readSet = new Set((reads ?? []).map((r) => r.announcement_id));
      const unread = (anns ?? []).filter((a) => !readSet.has(a.id)).slice(0, 3);
      if (!cancelled) {
        setPopups(unread);
        if (unread.length > 0) {
          try { localStorage.setItem(storageKey, today); } catch { /* noop */ }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const current = popups[0];

  const dismiss = React.useCallback(async () => {
    if (current && user) {
      await supabase.from("notification_reads").upsert(
        { user_id: user.id, announcement_id: current.id },
        { onConflict: "user_id,announcement_id" },
      ).then(() => undefined, () => undefined);
    }
    setPopups((prev) => (prev.length <= 1 ? [] : prev.slice(1)));
  }, [current, user]);

  React.useEffect(() => {
    if (!current) return;
    const timer = setTimeout(() => { dismiss(); }, 5000);
    return () => clearTimeout(timer);
  }, [current, dismiss]);

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={dismiss}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-card rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl overflow-hidden">
              <motion.div
                key={current.id}
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 5, ease: "linear" }}
                className="h-full"
                style={{ background: "var(--portal)" }}
              />
            </div>
            <button onClick={dismiss} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors">
              <X className="size-5" />
            </button>
            <div className="text-center">
              <Megaphone className="size-8 mx-auto mb-3" style={{ color: "var(--portal)" }} />
              <h3 className="font-display text-lg font-semibold mb-2">{current.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{current.body}</p>
              <div className="text-xs text-muted-foreground mt-4">
                {format(new Date(current.created_at), "HH:mm dd/MM/yyyy", { locale: vi })}
              </div>
            </div>
            {popups.length > 1 && (
              <div className="flex justify-center gap-1.5 mt-4">
                {popups.map((_, i) => (
                  <div key={i} className={cn("size-1.5 rounded-full transition-colors", i === 0 ? "bg-foreground" : "bg-muted")} />
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const PLAN_BADGE: Record<string, { label: string; color: string }> = {
  free: { label: "Miễn phí", color: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400" },
  tutor: { label: "Gia sư", color: "bg-amber-100 text-amber-700 dark:text-amber-300" },
  center: { label: "Trung tâm", color: "bg-blue-100 text-blue-700 dark:text-blue-300" },
  school: { label: "Trường học", color: "bg-emerald-100 text-emerald-700 dark:text-emerald-300" },
  enterprise: { label: "Enterprise", color: "bg-purple-100 text-purple-700" },
};

function SidebarPlanBadge({ collapsed }: { collapsed: boolean }) {
  const ent = useEntitlements();
  const plan = PLAN_BADGE[ent.tier] ?? PLAN_BADGE.free;

  if (collapsed) {
    return (
      <div className="px-2 py-2 border-t border-white/[0.06]">
        <div className={cn("size-9 mx-auto rounded-lg grid place-items-center text-[11px] font-bold", plan.color)} title={`Gói ${plan.label}`}>
          {plan.label.charAt(0)}
        </div>
      </div>
    );
  }

  const showUpgrade = ent.tier === "free" || ent.tier === "tutor";
  return (
    <div className="px-3 py-3 border-t border-white/[0.06]">
      <div className="px-3 py-2.5 rounded-xl bg-white/[0.04] ring-1 ring-white/[0.04]">
        <div className="text-[10px] uppercase tracking-widest text-white/30">Gói hiện tại</div>
        <div className="flex items-center justify-between mt-1.5 gap-2">
          <span className={cn("inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium", plan.color)}>
            {plan.label}
          </span>
          {showUpgrade && (
            <Link to="/billing" className="text-[11px] font-medium text-blue-400 hover:text-blue-300 transition-colors shrink-0">
              Nâng cấp →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function UpgradeButton() {
  const ent = useEntitlements();
  const { isDemo } = useAuth();
  if (isDemo) return null;
  if (ent.tier !== "free" && ent.tier !== "tutor") return null;
  return (
    <Link to="/billing">
      <Button
        size="sm"
        className="h-8 px-3.5 rounded-full text-xs bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90 active:scale-[0.98] transition-all duration-500 gap-1.5 shadow-[0_4px_16px_rgb(37,99,235,0.25)]"
      >
        <Zap className="size-3.5" strokeWidth={1.5} />
        <span className="hidden sm:inline">Nâng cấp</span>
      </Button>
    </Link>
  );
}


